import { getAuthInstance } from '@fusion/auth-v3'
import { getLogInAsHeaders, getLogInAsUrl, LogInAsHeaderNames } from '@fusion/saas-components-v3'
import type {
  Notification,
  Trigger,
  GetNotificationsParams,
  NewTrigger,
  PatchClearNotificationRequest,
  UpdateTrigger
} from '@/types/alerts'
import { DELETE, GET, PATCH, POST } from './helpers'

// Returns array of alert triggers

/*
 ** Success: 200
 ** Errors: 404 if devId for accountId is not found
 */
export const getAlerts = function (devId: string) {
  const auth = getAuthInstance()
  return GET<Trigger[]>(
    getLogInAsUrl(`/accounts/accountId/devices/${devId}/triggers`, auth),
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID])
    },
    {},
    APP_ENV.VITE_APP_ALERT_HOST
  )
}

// Returns alert trigger
/*
 ** Success: 200
 ** Error: 404 if devId for accountId is not found
 ** Error: 404 if triggerId for devId for accountId is not found
 */
export const getAlert = function (devId: string, triggerId: string) {
  const auth = getAuthInstance()
  return GET<Trigger>(
    getLogInAsUrl(`/accounts/accountId/devices/${devId}/triggers/${triggerId}`, auth),
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID])
    },
    {},
    APP_ENV.VITE_APP_ALERT_HOST
  )
}

// Creates alert trigger
/*
 ** Success: 201
 ** Error: 404 if devId for accountId is not found
 ** Error: 422 if trigger does not pass validation
 */
export const postAlert = function (alertData: NewTrigger, devId: string) {
  const body = JSON.stringify(alertData)
  const auth = getAuthInstance()
  return POST(
    getLogInAsUrl(`/accounts/accountId/devices/${devId}/triggers`, auth),
    body,
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID, LogInAsHeaderNames.USER_ID])
    },
    {},
    APP_ENV.VITE_APP_ALERT_HOST
  )
}

// Replaces alert trigger
/*
 ** Success: 204
 ** Error: 404 if devId for accountId is not found
 ** Error: 404 if triggerId for devId for accountID is not found
 */
export const patchAlert = function (alertData: UpdateTrigger, devId: string, triggerId: string) {
  const body = JSON.stringify(alertData)
  const auth = getAuthInstance()
  return PATCH(
    getLogInAsUrl(`/accounts/accountId/devices/${devId}/triggers/${triggerId}`, auth),
    body,
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID, LogInAsHeaderNames.USER_ID])
    },
    {},
    APP_ENV.VITE_APP_ALERT_HOST
  )
}

// Deletes alert trigger
/*
 ** Success: 204
 ** Error: 404 if devId for accountId is not found
 ** Error: 404 if triggerId for devId for accountId is not found
 */
export const deleteAlert = function (devId: string, triggerId: string) {
  const auth = getAuthInstance()
  return DELETE(
    getLogInAsUrl(`/accounts/accountId/devices/${devId}/triggers/${triggerId}`, auth),
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID])
    },
    {},
    APP_ENV.VITE_APP_ALERT_HOST
  )
}

export const getAlertNotifications = function (q?: GetNotificationsParams) {
  let query = ''
  if (q !== undefined) {
    query += '?'
    if (q.isRead !== undefined) {
      query += `isRead=${q.isRead}`
    }
    if (q.isRead !== undefined && q.limit !== undefined) {
      query += '&'
    }
    if (q.limit !== undefined) {
      query += `limit=${q.limit}`
    }
  }

  const auth = getAuthInstance()
  return GET<Notification[]>(
    getLogInAsUrl(`/accounts/accountId/notifications${query}`, auth),
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID, LogInAsHeaderNames.USER_ID])
    },
    {},
    APP_ENV.VITE_APP_ALERT_HOST
  )
}

export const patchClearNotification = function (noteId: string) {
  const payload: PatchClearNotificationRequest = { isRead: true }
  const body = JSON.stringify(payload)
  const auth = getAuthInstance()
  return PATCH(
    getLogInAsUrl(`/accounts/accountId/notifications/${noteId}`, auth),
    body,
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID, LogInAsHeaderNames.USER_ID])
    },
    {},
    APP_ENV.VITE_APP_ALERT_HOST
  )
}

export const postClearAllNotifications = function () {
  const auth = getAuthInstance()
  return POST(
    getLogInAsUrl(`/accounts/accountId/notifications/clear`, auth),
    null,
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID])
    },
    {},
    APP_ENV.VITE_APP_ALERT_HOST
  )
}

export const postClearAllDeviceNotifications = function (devId: string) {
  const auth = getAuthInstance()
  return POST(
    getLogInAsUrl(`/accounts/accountId/devices/${devId}/notifications/clear`, auth),
    null,
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID])
    },
    {},
    APP_ENV.VITE_APP_ALERT_HOST
  )
}

export const deleteAlertNotification = function (noteId: string) {
  const auth = getAuthInstance()
  return DELETE(
    getLogInAsUrl(`/accounts/accountId/notifications/${noteId}`, auth),
    {
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID, LogInAsHeaderNames.USER_ID])
    },
    {},
    APP_ENV.VITE_APP_ALERT_HOST
  )
}

/*
Schema:
Trigger
NOTES:
- Only ONE condition will be supported at this time.
- Only ONE recipient will be supported at this time.
- "field" must equal the field name sent by the device.
- "operator" valid values are:
    - "lt" -> Less Than
    - "lte" -> Less Than or Equal To
    - "eq" -> Equal To
    - "gte" -> Greater Than or Equal To
    - "gt" -> Greater Than
- "value" must be sent as a string
- valid boolean string values
    - "true" -> true
    - "false" -> false
- valid UTC timestamp string values must be either RFC3339 or ISO8601
    - example: "2020-06-12T17:13:10.095769Z"
- will NOT support "PATCH” operations
- conditions "alias" field is used for string substitutions for future support of multiple conditions
- only supports "in app" notifications as recipients at this time - "notification" is used to indicate "in app", additional options like "email" and "sms" will come later.
{
	"title":"My first alert",
	"message”:”Backyard PM 2.5 of {pm2.5|value} exceeded safe level of 2.5",
	"conditions": [
		{
			"field":"sps30_mcpm2x5",
			"operator":"gt",
			"value":”2.7”,
      "alias":"pm2.5",
      "enabled":true,
      "severity":"standard/warning/critical"
		}
	],
	"recipients":[
		{
			"userId":"bosp2mq23akg01o2fjd0",
			"notification":true
		}
	]
}

7/10 - There will be two properties, one called “enabled” that will be a boolean, and the other call “severity” that will be a string can have the following values (standard/warning/critical)
*/
